import React from 'react';

/**
 * The sign-in page's illustration: a care home, a carer with a resident, and
 * the two things the platform keeps an eye on (vitals, medication) floating
 * beside them. Pure inline SVG + CSS — no image request, crisp at any size.
 *
 * Every animation is gentle and decorative, and all of it stops for anyone who
 * has asked their system for reduced motion.
 */
const HEART = 'M6 11.2C2.6 8.6 0 6.5 0 3.6 0 1.6 1.5 0 3.4 0 4.5 0 5.5.6 6 1.5 6.5.6 7.5 0 8.6 0 10.5 0 12 1.6 12 3.6c0 2.9-2.6 5-6 7.6Z';

const LoginIllustration = ({ className = '' }) => (
  <div className={`sp-illus ${className}`}>
    <style>{`
      .sp-illus svg { overflow: visible; }
      .sp-illus .float-slow { animation: sp-float 6s ease-in-out infinite; }
      .sp-illus .float-a { animation: sp-float 5s ease-in-out infinite; }
      .sp-illus .float-b { animation: sp-float 5.5s ease-in-out -2.5s infinite; }
      .sp-illus .blob { animation: sp-breathe 9s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
      .sp-illus .blob-2 { animation: sp-breathe 11s ease-in-out -4s infinite; transform-box: fill-box; transform-origin: center; }
      .sp-illus .cloud { animation: sp-drift 14s ease-in-out infinite; }
      .sp-illus .cloud-2 { animation: sp-drift 18s ease-in-out -7s infinite; }
      .sp-illus .window { animation: sp-glow 4s ease-in-out infinite; }
      .sp-illus .window.w2 { animation-delay: -1s; }
      .sp-illus .window.w3 { animation-delay: -2s; }
      .sp-illus .window.w4 { animation-delay: -3s; }
      .sp-illus .beat { animation: sp-beat 1.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
      .sp-illus .rise { animation: sp-rise 4.5s ease-in infinite; transform-box: fill-box; transform-origin: center; opacity: 0; }
      .sp-illus .rise.r2 { animation-delay: -1.5s; }
      .sp-illus .rise.r3 { animation-delay: -3s; }
      .sp-illus .ecg { stroke-dasharray: 220; stroke-dashoffset: 220; animation: sp-ecg 2.6s linear infinite; }
      .sp-illus .sway { animation: sp-sway 5s ease-in-out infinite; transform-box: fill-box; transform-origin: bottom center; }
      .sp-illus .tick { stroke-dasharray: 20; stroke-dashoffset: 20; animation: sp-tick 3.2s ease-out infinite; }
      .sp-illus .wave-arm { animation: sp-wave 2.8s ease-in-out infinite; transform-box: fill-box; transform-origin: 0% 0%; }

      @keyframes sp-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      @keyframes sp-breathe { 0%, 100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.05) rotate(3deg); } }
      @keyframes sp-drift { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(24px); } }
      @keyframes sp-glow { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }
      @keyframes sp-beat { 0%, 100% { transform: scale(1); } 15% { transform: scale(1.18); } 30% { transform: scale(1); } 45% { transform: scale(1.1); } }
      @keyframes sp-rise { 0% { transform: translateY(0) scale(.6); opacity: 0; } 15% { opacity: .95; } 100% { transform: translateY(-70px) scale(1.05); opacity: 0; } }
      @keyframes sp-ecg { to { stroke-dashoffset: 0; } }
      @keyframes sp-sway { 0%, 100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
      @keyframes sp-tick { 0%, 30% { stroke-dashoffset: 20; } 55%, 100% { stroke-dashoffset: 0; } }
      @keyframes sp-wave { 0%, 60%, 100% { transform: rotate(0deg); } 70% { transform: rotate(-14deg); } 80% { transform: rotate(6deg); } 90% { transform: rotate(-10deg); } }

      @media (prefers-reduced-motion: reduce) {
        .sp-illus * { animation: none !important; }
        .sp-illus .rise { opacity: .8; }
        .sp-illus .ecg, .sp-illus .tick { stroke-dashoffset: 0; }
      }
    `}</style>

    <svg viewBox="0 0 520 440" width="100%" role="img" aria-label="A carer walking with a resident outside a care home">
      <defs>
        <filter id="sp-card-shadow" x="-20%" y="-30%" width="140%" height="170%">
          <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#0F172A" floodOpacity=".12" />
        </filter>
        <linearGradient id="sp-roof" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1D5BC4" />
          <stop offset="1" stopColor="#0B3F8F" />
        </linearGradient>
        <linearGradient id="sp-wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#EEF4FD" />
        </linearGradient>
      </defs>

      {/* Soft backdrop */}
      <circle className="blob" cx="250" cy="250" r="175" fill="#3B82F6" opacity=".08" />
      <circle className="blob-2" cx="390" cy="300" r="95" fill="#14B8A6" opacity=".09" />

      {/* Clouds */}
      <g className="cloud" fill="#DCE7F8" opacity=".95">
        <ellipse cx="226" cy="44" rx="30" ry="12" />
        <ellipse cx="246" cy="36" rx="22" ry="14" />
      </g>
      <g className="cloud-2" fill="#DCE7F8" opacity=".9">
        <ellipse cx="60" cy="168" rx="24" ry="9" />
        <ellipse cx="74" cy="162" rx="16" ry="11" />
      </g>

      {/* Ground */}
      <ellipse cx="270" cy="394" rx="215" ry="16" fill="#0B3F8F" opacity=".08" />

      {/* ── The care home ── */}
      <g className="float-slow">
        <rect x="104" y="176" width="212" height="214" rx="6" fill="url(#sp-wall)" stroke="#D3E0F4" strokeWidth="2" />
        <path d="M90 182 L210 100 L330 182 Z" fill="url(#sp-roof)" />
        <rect x="270" y="112" width="20" height="40" rx="3" fill="#0B3F8F" />
        {/* Heart on the gable */}
        <circle cx="210" cy="150" r="17" fill="#FFFFFF" />
        <g transform="translate(201 144) scale(1.5)"><path className="beat" d={HEART} fill="#F43F5E" /></g>

        {/* Windows */}
        <rect className="window" x="126" y="204" width="42" height="38" rx="5" fill="#FDE68A" stroke="#C9D8EF" strokeWidth="2" />
        <rect className="window w2" x="252" y="204" width="42" height="38" rx="5" fill="#FDE68A" stroke="#C9D8EF" strokeWidth="2" />
        <rect className="window w3" x="126" y="266" width="42" height="38" rx="5" fill="#FDE68A" stroke="#C9D8EF" strokeWidth="2" />
        <rect className="window w4" x="252" y="266" width="42" height="38" rx="5" fill="#FDE68A" stroke="#C9D8EF" strokeWidth="2" />
        <path d="M147 204v38M126 223h42M273 204v38M252 223h42M147 266v38M126 285h42M273 266v38M252 285h42" stroke="#C9D8EF" strokeWidth="2" />

        {/* Door + canopy */}
        <path d="M184 312h52v78h-52z" fill="#2563EB" />
        <path d="M184 312a26 26 0 0 1 52 0z" fill="#1D4ED8" />
        <circle cx="227" cy="352" r="3" fill="#FDE68A" />
        <rect x="176" y="302" width="68" height="8" rx="4" fill="#0B3F8F" />
        {/* Care sign */}
        <rect x="190" y="182" width="40" height="16" rx="8" fill="#14B8A6" />
        <path d="M206 190h8M210 186v8" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
      </g>

      {/* Plant by the door */}
      <g>
        <path d="M66 392h30l-4-26H70z" fill="#F59E0B" />
        <g className="sway">
          <path d="M81 366c-2-18-14-26-22-30 10 0 20 8 22 30z" fill="#22C55E" />
          <path d="M81 366c2-20 12-30 22-34-6 10-14 18-22 34z" fill="#16A34A" />
          <path d="M81 366c0-14 2-24 0-36 4 10 4 22 0 36z" fill="#4ADE80" />
        </g>
      </g>

      {/* ── Resident with walking stick ── */}
      <g>
        <ellipse cx="368" cy="392" rx="30" ry="5" fill="#0F172A" opacity=".08" />
        {/* legs */}
        <rect x="356" y="334" width="10" height="52" rx="5" fill="#475569" />
        <rect x="370" y="334" width="10" height="52" rx="5" fill="#475569" />
        <ellipse cx="360" cy="388" rx="9" ry="4" fill="#1E293B" />
        <ellipse cx="376" cy="388" rx="9" ry="4" fill="#1E293B" />
        {/* cardigan */}
        <rect x="348" y="282" width="40" height="60" rx="16" fill="#8B5CF6" />
        <path d="M368 284v56" stroke="#7C3AED" strokeWidth="2" />
        <circle cx="368" cy="300" r="1.8" fill="#EDE9FE" />
        <circle cx="368" cy="314" r="1.8" fill="#EDE9FE" />
        {/* arm + cane */}
        <path d="M386 294c6 10 8 22 6 36" stroke="#8B5CF6" strokeWidth="9" strokeLinecap="round" fill="none" />
        <circle cx="392" cy="332" r="5" fill="#F5C9A5" />
        <path d="M392 330c0-8 10-8 10 0" stroke="#92400E" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M394 332l6 56" stroke="#92400E" strokeWidth="4" strokeLinecap="round" />
        {/* head */}
        <circle cx="368" cy="264" r="16" fill="#F5C9A5" />
        <path d="M352 262c0-12 8-18 16-18s16 6 16 18c-3-6-9-9-16-9s-13 3-16 9z" fill="#E2E8F0" />
        <circle cx="368" cy="244" r="7" fill="#E2E8F0" />
        <circle cx="362" cy="266" r="4" fill="none" stroke="#64748B" strokeWidth="1.5" />
        <circle cx="374" cy="266" r="4" fill="none" stroke="#64748B" strokeWidth="1.5" />
        <path d="M366 266h4" stroke="#64748B" strokeWidth="1.5" />
        <path d="M364 274c2 2 6 2 8 0" stroke="#B45309" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      </g>

      {/* ── Carer ── */}
      <g>
        <ellipse cx="430" cy="392" rx="30" ry="5" fill="#0F172A" opacity=".08" />
        <rect x="418" y="330" width="11" height="56" rx="5" fill="#0F766E" />
        <rect x="433" y="330" width="11" height="56" rx="5" fill="#0F766E" />
        <ellipse cx="423" cy="388" rx="9" ry="4" fill="#FFFFFF" stroke="#CBD5E1" />
        <ellipse cx="439" cy="388" rx="9" ry="4" fill="#FFFFFF" stroke="#CBD5E1" />
        {/* scrubs */}
        <rect x="410" y="270" width="42" height="68" rx="16" fill="#14B8A6" />
        <path d="M424 272l7 12 7-12" stroke="#0F766E" strokeWidth="3" fill="none" strokeLinejoin="round" />
        <rect x="438" y="294" width="9" height="12" rx="2" fill="#FFFFFF" />
        {/* arm round the resident's shoulder */}
        <path d="M414 284c-10 4-20 6-28 6" stroke="#14B8A6" strokeWidth="9" strokeLinecap="round" fill="none" />
        <circle cx="384" cy="290" r="5" fill="#E0A47E" />
        {/* other arm, waving now and then */}
        <g className="wave-arm">
          <path d="M450 284c8 -4 12 -16 12 -26" stroke="#14B8A6" strokeWidth="9" strokeLinecap="round" fill="none" />
          <circle cx="462" cy="254" r="5" fill="#E0A47E" />
        </g>
        {/* head */}
        <circle cx="431" cy="250" r="16" fill="#E0A47E" />
        <path d="M415 250c-1-12 7-20 16-20 10 0 17 7 16 18-4-6-10-9-16-9-7 0-12 4-16 11z" fill="#1F2937" />
        <path d="M446 244c6 4 8 12 4 20" stroke="#1F2937" strokeWidth="6" strokeLinecap="round" fill="none" />
        <circle cx="425" cy="252" r="1.6" fill="#1F2937" />
        <circle cx="436" cy="252" r="1.6" fill="#1F2937" />
        <path d="M427 259c2 2 6 2 8 0" stroke="#9A3412" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      </g>

      {/* Hearts rising between them */}
      <g transform="translate(394 232)"><path className="rise" d={HEART} fill="#F43F5E" /></g>
      <g transform="translate(406 222) scale(.8)"><path className="rise r2" d={HEART} fill="#FB7185" /></g>
      <g transform="translate(384 216) scale(.7)"><path className="rise r3" d={HEART} fill="#F43F5E" /></g>

      {/* ── Vitals card ── */}
      <g className="float-a" filter="url(#sp-card-shadow)">
        <g transform="translate(-6 -134)">
          <rect x="18" y="196" width="150" height="70" rx="16" fill="#FFFFFF" />
          <circle cx="44" cy="222" r="13" fill="#FFE4E6" />
          <g transform="translate(38 216)"><path className="beat" d={HEART} fill="#F43F5E" /></g>
          <text x="64" y="219" fontSize="10" fill="#64748B" fontFamily="inherit">Heart rate</text>
          <text x="64" y="233" fontSize="13" fontWeight="700" fill="#0F172A" fontFamily="inherit">72 bpm</text>
          <path className="ecg" d="M30 252h28l6-10 8 18 7-14 5 6h56" stroke="#F43F5E" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>

      {/* ── Medication card ── */}
      <g className="float-b" filter="url(#sp-card-shadow)">
        <rect x="350" y="120" width="158" height="58" rx="16" fill="#FFFFFF" />
        <circle cx="376" cy="149" r="13" fill="#DCFCE7" />
        <path className="tick" d="M370 149l4.5 4.5 8-9" stroke="#16A34A" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="396" y="145" fontSize="12" fontWeight="700" fill="#0F172A" fontFamily="inherit">Medication given</text>
        <text x="396" y="160" fontSize="10" fill="#64748B" fontFamily="inherit">08:00 · on time</text>
      </g>
    </svg>
  </div>
);

export default LoginIllustration;
